import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertOctagon, ChevronDown, ExternalLink, Loader2, RefreshCw, ThumbsDown } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

const KIND_STYLE = {
  paused: 'text-orange-300 border-orange-500/40 bg-orange-500/10',
  cancelled: 'text-red-300 border-red-500/40 bg-red-500/10',
  completed: 'text-violet-300 border-violet-500/40 bg-violet-500/10',
  no_sequence: 'text-rose-300 border-rose-500/40 bg-rose-500/10',
};
const KIND_LABEL = {
  paused: 'Pausiert', cancelled: 'Gestoppt', completed: 'Sequenz zu Ende', no_sequence: 'Keine Sequenz',
};
const STAGE_LABEL = { contacted: 'Contacted', response_received: 'Response Received' };

function StalledRow({ it, busy, onAction }) {
  const cls = KIND_STYLE[it.kind] || 'text-muted-foreground border-border';
  return (
    <div className="px-4 py-2.5 border-t border-border/40 flex items-start gap-3 flex-wrap" data-testid={`stalled-row-${it.project_id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/projects/${it.project_id}`} className="text-sm font-medium text-foreground hover:underline inline-flex items-center gap-1"
            data-testid={`stalled-open-${it.project_id}`}>
            {it.project_name || it.random_project_name || 'Deal'} <ExternalLink className="w-3 h-3 text-muted-foreground" />
          </Link>
          <span className={`text-[9px] font-mono border rounded px-1.5 py-0.5 ${cls}`}>{KIND_LABEL[it.kind] || it.kind}</span>
          <span className="text-[9px] font-mono border border-border rounded px-1.5 py-0.5 text-muted-foreground">{STAGE_LABEL[it.stage] || it.stage}</span>
          {it.days_idle != null && (
            <span className={`text-[10px] font-mono ${it.days_idle >= 21 ? 'text-red-300' : 'text-muted-foreground'}`} data-testid={`stalled-idle-${it.project_id}`}>
              {it.days_idle} Tage ohne Aktivität
            </span>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`stalled-reason-${it.project_id}`}>
          {it.reason}{it.contact_name ? ` · ${it.contact_name}` : ''}
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button onClick={() => onAction(it, 'pass')} disabled={busy === it.project_id} data-testid={`stalled-pass-${it.project_id}`}
          className="inline-flex items-center gap-1 text-[11px] border border-red-500/40 text-red-300 hover:bg-red-500/10 rounded-sm px-2 h-7 disabled:opacity-50">
          {busy === it.project_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ThumbsDown className="w-3 h-3" />} Pass vorschlagen
        </button>
      </div>
    </div>
  );
}

// "No deal without a next step" (Max, 11.09.2026): outreach deals with nothing scheduled — no pending
// sequence step, no open task, no pass proposal, no positive reply.
export function StalledOutreachCard({ onChanged }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState('');
  const load = useCallback(() => api.getStalledOutreach().then(r => setData(r.data)).catch(() => setData({ items: [], auto_pass_pending: 0 })), []);
  useEffect(() => { load(); }, [load]);

  const act = async (it) => {
    setBusy(it.project_id);
    try {
      const reason = window.prompt('Begründung für den Pass-Vorschlag:', 'Keine Reaktion auf Brief und Follow-ups — Outreach abgeschlossen');
      if (reason === null) { setBusy(''); return; }
      if (!reason.trim()) { toast.error('Begründung erforderlich'); setBusy(''); return; }
      const res = await api.turnDownProject(it.project_id, reason.trim(), false, 'stalled_outreach', ['no_response']);
      toast.success(res.data?.message || 'Pass vorgeschlagen');
      await load();
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    setBusy('');
  };

  const items = data?.items || [];
  return (
    <div className="bg-card border border-border rounded-sm" data-testid="stalled-outreach-card">
      <div className="px-4 py-3 flex items-center gap-2 flex-wrap">
        <button onClick={() => setOpen(o => !o)} className="inline-flex items-center gap-2 text-sm font-medium text-foreground" data-testid="stalled-outreach-toggle">
          <AlertOctagon className={`w-4 h-4 ${items.length ? 'text-amber-400' : 'text-emerald-400'}`} />
          Deals ohne nächsten Schritt
          <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {data && (
          <span className={`text-[10px] font-mono border rounded px-1.5 py-0.5 ${items.length ? 'text-amber-300 border-amber-500/40 bg-amber-500/10' : 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'}`}
            data-testid="stalled-outreach-count">{items.length}</span>
        )}
        <span className="text-[10px] text-muted-foreground">
          Contacted / Response Received · kein offener Sequenz-Schritt, keine Aufgabe, kein Pass-Vorschlag, keine positive Antwort
        </span>
        <button onClick={load} title="Aktualisieren" data-testid="stalled-outreach-refresh"
          className="ml-auto text-muted-foreground hover:text-foreground"><RefreshCw className="w-3.5 h-3.5" /></button>
      </div>
      {open && (data === null ? (
        <div className="flex justify-center py-5 border-t border-border/40"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <p className="text-xs text-muted-foreground px-4 py-4 border-t border-border/40" data-testid="stalled-outreach-empty">
          Alles hat einen nächsten Schritt.{data.auto_pass_pending ? ` ${data.auto_pass_pending} Deal${data.auto_pass_pending === 1 ? '' : 's'} mit beendeter Sequenz — Pass-Vorschlag folgt ${data.pass_suggest_days || 14} Tage nach dem letzten Schritt.` : ''}
        </p>
      ) : (
        <>
          {items.map(it => <StalledRow key={it.project_id} it={it} busy={busy} onAction={act} />)}
          {data.auto_pass_pending > 0 && (
            <p className="text-[10px] text-muted-foreground px-4 py-2 border-t border-border/40" data-testid="stalled-autopass-note">
              + {data.auto_pass_pending} Deal{data.auto_pass_pending === 1 ? '' : 's'} mit beendeter Sequenz — Pass-Vorschlag an Max folgt {data.pass_suggest_days || 14} Tage nach dem letzten Schritt ohne Reaktion.
            </p>
          )}
        </>
      ))}
    </div>
  );
}
